import { SmoothLink as Link } from "@/components/navigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { getEntityImageUrl, generateAltText } from "@/lib/image-utils";
import { MapPin, Star, Award, ArrowRight, Building2 } from "lucide-react";
import type { Hospital } from "@/types/schema";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

interface HospitalCardProps {
  hospital: Hospital;
}

export function HospitalCard({ hospital }: HospitalCardProps) {
  const { trackHospitalView } = useAnalyticsContext();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  const imageUrl = getEntityImageUrl(hospital.image_url, hospital.name, "building", 600, 338);
  const altText = generateAltText(hospital.name, "hospital");

  return (
    <Card className="group overflow-hidden hover:shadow-hover transition-all duration-300">
      <div className="relative overflow-hidden">
        <AspectRatio ratio={16 / 9}>
          {isLoading && hospital.image_url && (
            <Skeleton className="absolute inset-0 w-full h-full" />
          )}
          
          {imageUrl && !hasError ? (
            <img
              src={imageUrl}
              alt={altText}
              width={600}
              height={338}
              loading="lazy"
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
              className={cn(
                "w-full h-full object-cover group-hover:scale-105 transition-transform duration-300",
                isLoading ? "opacity-0" : "opacity-100"
              )}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-secondary to-secondary/80 flex items-center justify-center">
              <Building2 className="w-24 h-24 text-secondary-foreground/30" />
            </div>
          )}
        </AspectRatio>

        {hospital.is_featured && (
          <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">
            Featured Partner
          </Badge>
        )}
        
        {/* Accreditations overlay */}
        {hospital.accreditations && hospital.accreditations.length > 0 && (
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex flex-wrap gap-2">
              {hospital.accreditations.map((acc) => (
                <div 
                  key={acc} 
                  className="inline-flex items-center rounded-full border border-white/30 px-2.5 py-0.5 text-xs font-semibold bg-background/60 backdrop-blur-md text-foreground shadow-sm"
                >
                  <Award className="w-3 h-3 mr-1" />
                  {acc}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display text-2xl font-semibold text-secondary group-hover:text-primary transition-colors">
            {hospital.name}
          </h3>
          <div className="flex items-center gap-1 text-accent">
            <Star className="w-5 h-5 fill-accent" />
            <span className="font-semibold">{hospital.rating}</span>
            {hospital.review_count !== undefined && (
              <span className="text-muted-foreground text-sm">({hospital.review_count})</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-muted-foreground mb-4">
          <MapPin className="w-4 h-4" />
          <span>{hospital.city}</span>
        </div>

        {hospital.description ? (
          <p className="text-muted-foreground mb-4 line-clamp-2">
            {hospital.description}
          </p>
        ) : null}

        {/* Specialties */}
        {hospital.specialties && Array.isArray(hospital.specialties) && hospital.specialties.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {hospital.specialties.slice(0, 4).map((spec) => (
              <Badge key={spec} variant="outline">
                {spec}
              </Badge>
            ))}
          </div>
        )}

        <Link to={`/hospitals/${hospital.slug}`}>
          <Button 
            className="w-full rounded-full"
            onClick={() => trackHospitalView(hospital.name, hospital.city)}
          >
            View Hospital
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export function HospitalCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <AspectRatio ratio={16 / 9}>
        <Skeleton className="w-full h-full" />
      </AspectRatio>
      <CardContent className="p-6 space-y-4">
        <div className="flex justify-between">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-5 w-16" />
        </div>
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-16 w-full" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-18" />
        </div>
        <Skeleton className="h-10 w-full" />
      </CardContent>
    </Card>
  );
}
